export enum FilterTreeOperator {
  And = 'and',
  Or = 'or',
}

export enum FilterTreeTextMatch {
  Contains = 'contains',
  Equals = 'equals',
  NotContains = 'not_contains',
  NotEquals = 'not_equals',
}

// One filter-menu field. `values` holds that field's flat filter keys — `field`,
// `${field}Min`/`Max` or `${field}From`/`To` — so every leaf reuses the flat filter
// semantics (inputs, chips, SQL) unchanged. `match` applies to custom text fields and report text columns.
export interface IFilterTreeCondition {
  field: string
  values: Record<string, unknown>
  match?: FilterTreeTextMatch
}

export interface IFilterTreeGroup {
  operator: FilterTreeOperator
  conditions: IFilterTreeNode[]
}

export type IFilterTreeNode = IFilterTreeCondition | IFilterTreeGroup

// Root plus one level of groups: deeper nesting stops reading clearly in a popover.
export const FILTER_TREE_MAX_DEPTH = 2

export const FILTER_TREE_MAX_CONDITIONS = 50

export const EMPTY_FILTER_TREE: IFilterTreeGroup = {
  operator: FilterTreeOperator.And,
  conditions: [],
}

export const isFilterTreeGroup = (node: IFilterTreeNode): node is IFilterTreeGroup =>
  'conditions' in node

const isFilterValueSet = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.some((item) => item !== null && item !== undefined && item !== '')
  }
  return value !== null && value !== undefined && value !== ''
}

export const isFilterTreeConditionSet = (condition: IFilterTreeCondition): boolean =>
  Object.values(condition.values || {}).some(isFilterValueSet)

// Drops unset conditions and the groups they leave empty, so a half-built row
// never narrows the list.
export const pruneFilterTree = (group: IFilterTreeGroup): IFilterTreeGroup => ({
  operator: group.operator,
  conditions: group.conditions
    .map((node) => (isFilterTreeGroup(node) ? pruneFilterTree(node) : node))
    .filter((node) =>
      isFilterTreeGroup(node) ? node.conditions.length > 0 : isFilterTreeConditionSet(node),
    ),
})

export const countFilterTreeConditions = (group: IFilterTreeGroup | null | undefined): number =>
  (group?.conditions || []).reduce(
    (count, node) =>
      count +
      (isFilterTreeGroup(node)
        ? countFilterTreeConditions(node)
        : isFilterTreeConditionSet(node)
          ? 1
          : 0),
    0,
  )

// Drops the conditions on fields `shouldDrop` names — e.g. ones the table no longer shows.
export const dropFilterTreeFields = (
  group: IFilterTreeGroup,
  shouldDrop: (field: string) => boolean,
): IFilterTreeGroup =>
  pruneFilterTree({
    operator: group.operator,
    conditions: group.conditions
      .map((node) => (isFilterTreeGroup(node) ? dropFilterTreeFields(node, shouldDrop) : node))
      .filter((node) => isFilterTreeGroup(node) || !shouldDrop(node.field)),
  })

const FIELD_NAME_PATTERN = /^[A-Za-z0-9_-]{1,100}$/
const OPERATORS = new Set<string>(Object.values(FilterTreeOperator))
const TEXT_MATCHES = new Set<unknown>(Object.values(FilterTreeTextMatch))

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const isScalar = (value: unknown) =>
  value === null || ['string', 'number', 'boolean'].includes(typeof value)

const isValidValue = (value: unknown) =>
  isScalar(value) || (Array.isArray(value) && value.every((item) => typeof item === 'string'))

// Shape check only: which fields exist and what their values mean is the compiler's job.
export const isValidFilterTree = (tree: unknown): tree is IFilterTreeGroup => {
  let conditionCount = 0
  const isValidNode = (node: unknown, depth: number): boolean => {
    if (!isPlainObject(node)) {
      return false
    }
    if ('conditions' in node) {
      return (
        depth <= FILTER_TREE_MAX_DEPTH &&
        OPERATORS.has(node.operator as string) &&
        Array.isArray(node.conditions) &&
        node.conditions.every((child) => isValidNode(child, depth + 1))
      )
    }
    conditionCount += 1
    return (
      typeof node.field === 'string' &&
      FIELD_NAME_PATTERN.test(node.field) &&
      isPlainObject(node.values) &&
      Object.values(node.values).every(isValidValue) &&
      (node.match === undefined || TEXT_MATCHES.has(node.match))
    )
  }
  return (
    isPlainObject(tree) &&
    'conditions' in tree &&
    isValidNode(tree, 1) &&
    conditionCount <= FILTER_TREE_MAX_CONDITIONS
  )
}

// Accepts the tree as an object or as the JSON string the list endpoints receive over GET.
export const parseFilterTree = (value: unknown): IFilterTreeGroup | null => {
  let parsed = value
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value) as unknown
    } catch {
      return null
    }
  }
  return isValidFilterTree(parsed) ? parsed : null
}

// A list's request filters carry the tree as `filterTree`, outside any filter config's types.
export const filterTreeFromFilters = (
  filters: object | null | undefined,
): IFilterTreeGroup | null =>
  parseFilterTree((filters as { filterTree?: unknown } | null | undefined)?.filterTree)
